
"use client";

import { authClient } from "~/server/better-auth/client";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Link, useRouter } from "~/i18n/navigation";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";

export function UserButton() {
    const t = useTranslations("Auth");
    const tNav = useTranslations("Nav");
    const { data: session, isPending } = authClient.useSession();
    const router = useRouter();

    if (isPending) {
        return (
            <Button variant="ghost" size="sm" disabled>
                <Loader2 className="h-4 w-4 animate-spin" />
            </Button>
        );
    }

    if (!session) {
        return (
          <div className="flex items-center gap-1 shrink-0 sm:gap-1.5 md:gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-1.5 text-xs whitespace-nowrap sm:px-3 md:h-9 md:px-4 md:text-sm"
              asChild
            >
              <Link href="/sign-in">{t("signIn")}</Link>
            </Button>
            <Button
              size="sm"
              className="h-8 px-2 text-xs whitespace-nowrap sm:px-3 md:h-9 md:px-4 md:text-sm"
              asChild
            >
              <Link href="/sign-up">{t("signUp")}</Link>
            </Button>
          </div>
        );
    }

    const handleSignOut = async () => {
        await authClient.signOut({
            fetchOptions: {
                onSuccess: () => {
                    router.push("/sign-in");
                    router.refresh();
                },
            },
        });
    };

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-8 w-8 rounded-full">
            <Avatar className="h-8 w-8">
              <AvatarImage
                src={session.user.image ?? ""}
                alt={`${session.user.name ?? "User"} Profile Avatar`}
              />
              <AvatarFallback>
                {session.user.name?.charAt(0) ?? "U"}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm leading-none font-medium">
                {session.user.name}
              </p>
              <p className="text-muted-foreground text-xs leading-none">
                {session.user.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href="/dashboard">{tNav("dashboard")}</Link>
          </DropdownMenuItem>
          {/* Explicitly check role. */}
          {(session.user as unknown as { role?: string }).role === "admin" && (
            <DropdownMenuItem asChild>
              <Link href="/admin">{tNav("admin")}</Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut}>{t("signOut")}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
}
